import axios from 'axios';

export const API_BASE = process.env.REACT_APP_API_URL || '';

export const api = axios.create({
  baseURL: API_BASE,
});

export interface Project {
  id: number;
  name: string;
  slug: string;
  address: string | null;
  created_at: string;
}

export interface ProjectUpdate {
  name?: string;
  address?: string;
}

export interface Room {
  id: number;
  project_id: number;
  story_id: number | null;
  number: string;
  name: string | null;
  is_exterior: boolean;
}

export interface DocumentSet {
  id: number;
  project_id: number;
  name: string;
  latest_document_id?: number | null;
}

export interface Task {
  id: number;
  room_id: number;
  trade_id: number | null;
  description: string;
  status: 'open' | 'done' | 'verified';
  created_by: string | null;
  completed_by: string | null;
  photo_url: string | null;
  photo_tag: 'description' | 'complete' | null;
}

export interface TaskWithDetails {
  id: number;
  room_id: number;
  trade_id: number | null;
  description: string;
  status: 'open' | 'done' | 'verified';
  created_by: string | null;
  completed_by: string | null;
  verified_by: string | null;
  due_date: string | null;
  created_at: string;
  completed_at: string | null;
  verified_at: string | null;
  photo_url: string | null;
  photo_tag: 'description' | 'complete' | null;
  room_number: string;
  room_name: string | null;
  story_id: number | null;
  story_name: string | null;
  trade_name: string | null;
}

export interface Note {
  id: number;
  room_id: number;
  content: string;
  author_name: string;
  created_at: string;
  photo_url: string | null;
}

export interface NoteRoomDetail {
  room_id: number;
  room_number: string;
  room_name: string | null;
}

export interface NoteWithDetails {
  id: number;
  content: string;
  author_name: string;
  created_at: string;
  rooms: NoteRoomDetail[];
  photo_url: string | null;
}

export interface NoteWithRooms {
  id: number;
  content: string;
  author_name: string;
  created_at: string;
  room_ids: number[];
}

export interface Trade {
  id: number;
  name: string;
}

export interface Story {
  id: number;
  project_id: number;
  name: string;
  floor_number: number;
}

export interface AttachmentRoomMapping {
  id: number;
  room_id: number;
}

export interface Attachment {
  id: number;
  project_id: number;
  filename: string;
  display_name: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  room_mappings: AttachmentRoomMapping[];
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DetectedRoom {
  number: string;
  name: string | null;
}

export interface PageMapping {
  id: number;
  document_id: number;
  page_number: number;
  room_id: number;
  label: string | null;
  document?: Document;
}

export interface Document {
  id: number;
  document_set_id: number;
  filename: string;
  file_path: string;
  file_type: string;
  page_count: number;
  created_at: string;
  version: number;
  is_current: boolean;
  status: 'active' | 'pending_review' | 'archived';
  conversion_status: 'pending' | 'converting' | 'ready' | 'failed';
  conversion_progress: number;
  conversion_error: string | null;
  conversion_warnings?: string | null;
  mapped_pages_count: number;
}

export interface UploadReplaceResponse {
  document: Document;
  requires_review: boolean;
  old_page_count: number | null;
  new_page_count: number;
  message: string;
}

export async function getRoom(projectId: number, roomId: number): Promise<Room> {
  const response = await api.get(`/api/projects/${projectId}/rooms/${roomId}`);
  return response.data;
}

export async function getRoomBySlug(projectSlug: string, roomNumber: string): Promise<Room> {
  const response = await api.get(`/api/projects/by-slug/${projectSlug}/rooms/${roomNumber}`);
  return response.data;
}

export async function getRoomsBySlug(projectSlug: string): Promise<Room[]> {
  const response = await api.get(`/api/projects/by-slug/${projectSlug}/rooms`);
  return response.data;
}

export async function getProjectSlug(projectId: number): Promise<string> {
  const response = await api.get(`/api/projects/${projectId}/slug`);
  return response.data.slug;
}

export async function getRoomAuthenticated(token: string, projectId: number, roomId: number): Promise<Room> {
  const response = await api.get(`/api/projects/${projectId}/rooms/${roomId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getDocumentSets(projectId: number): Promise<DocumentSet[]> {
  const response = await api.get(`/api/projects/${projectId}/document-sets`);
  return response.data;
}

export async function getDocumentSetsAuthenticated(token: string, projectId: number): Promise<DocumentSet[]> {
  const response = await api.get(`/api/projects/${projectId}/document-sets`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getRoomTasks(roomId: number): Promise<Task[]> {
  const response = await api.get(`/api/rooms/${roomId}/tasks`);
  return response.data;
}

export async function getRoomNotes(roomId: number): Promise<Note[]> {
  const response = await api.get(`/api/rooms/${roomId}/notes`);
  return response.data;
}

export async function getTrades(): Promise<Trade[]> {
  const response = await api.get(`/api/trades`);
  return response.data;
}

export async function createTask(roomId: number, description: string, tradeId: number | null, createdBy: string | null, dueDate: string | null = null): Promise<Task> {
  const response = await api.post(`/api/rooms/${roomId}/tasks`, {
    description,
    trade_id: tradeId,
    created_by: createdBy,
    due_date: dueDate,
  });
  return response.data;
}

export async function createTaskAuth(
  token: string,
  roomId: number,
  description: string,
  tradeId: number | null,
  createdBy: string | null,
  dueDate: string | null = null
): Promise<Task> {
  const response = await api.post(`/api/rooms/${roomId}/tasks`, {
    description,
    trade_id: tradeId,
    created_by: createdBy,
    due_date: dueDate || null,
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function markTaskDone(taskId: number, completedBy: string): Promise<Task> {
  const response = await api.patch(`/api/tasks/${taskId}/done`, { completed_by: completedBy });
  return response.data;
}

export async function reopenTask(taskId: number): Promise<Task> {
  const response = await api.patch(`/api/tasks/${taskId}/reopen`);
  return response.data;
}

export async function createNote(roomId: number, content: string, authorName: string): Promise<Note> {
  const response = await api.post(`/api/rooms/${roomId}/notes`, {
    content,
    author_name: authorName,
  });
  return response.data;
}

export async function getProjects(token: string): Promise<Project[]> {
  const response = await api.get('/api/projects', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function createProject(token: string, name: string, address?: string): Promise<Project> {
  const response = await api.post('/api/projects',
    { name, address },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function deleteProject(token: string, projectId: number): Promise<void> {
  await api.delete(`/api/projects/${projectId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function updateProject(token: string, projectId: number, updates: ProjectUpdate): Promise<Project> {
  const response = await api.patch(`/api/projects/${projectId}`, updates, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getRooms(token: string, projectId: number): Promise<Room[]> {
  const response = await api.get(`/api/projects/${projectId}/rooms`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function createRoom(
  token: string,
  projectId: number,
  number: string,
  name?: string,
  isExterior?: boolean
): Promise<Room> {
  const payload: { number: string; name?: string; is_exterior?: boolean } = { number, name };
  if (typeof isExterior === 'boolean') {
    payload.is_exterior = isExterior;
  }

  const response = await api.post(`/api/projects/${projectId}/rooms`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function deleteRoom(token: string, projectId: number, roomId: number): Promise<void> {
  await api.delete(`/api/projects/${projectId}/rooms/${roomId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function getRoomsForProject(token: string, projectId: number): Promise<Room[]> {
  const response = await api.get(`/api/projects/${projectId}/rooms`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function updateRoom(
  token: string,
  projectId: number,
  roomId: number,
  data: {
    number?: string;
    name?: string | null;
    story_id?: number | null;
    is_exterior?: boolean;
  }
): Promise<Room> {
  const response = await api.patch(`/api/projects/${projectId}/rooms/${roomId}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function createRoomsBulk(
  token: string,
  projectId: number,
  rooms: Array<{ number: string; name?: string | null }>
): Promise<Room[]> {
  const response = await api.post(`/api/projects/${projectId}/rooms/bulk`,
    { rooms },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export interface OCRDetectedRoomWithPage {
  number: string;
  name: string | null;
  page_number: number;
}

export interface OCRResultRoomResponse {
  room_id: number;
  room_number: string;
  room_name: string | null;
  page_number: number;
  mapping_id: number;
}

export interface OCRResultResponse {
  created_rooms: OCRResultRoomResponse[];
  existing_rooms: OCRResultRoomResponse[];
}

export async function createRoomsFromOCR(
  token: string,
  projectId: number,
  documentId: number,
  rooms: OCRDetectedRoomWithPage[],
  label?: string
): Promise<OCRResultResponse> {
  const response = await api.post(`/api/projects/${projectId}/rooms/from-ocr`,
    { document_id: documentId, rooms, label },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function createPageMapping(
  token: string,
  roomId: number,
  documentId: number,
  pageNumber: number,
  label?: string
): Promise<PageMapping> {
  const response = await api.post(`/api/rooms/${roomId}/page-mappings`,
    { document_id: documentId, page_number: pageNumber, label },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function getPageMappings(roomId: number): Promise<PageMapping[]> {
  const response = await api.get(`/api/rooms/${roomId}/page-mappings`);
  return response.data;
}

export async function getPageMappingsAuthenticated(token: string, roomId: number): Promise<PageMapping[]> {
  const response = await api.get(`/api/rooms/${roomId}/page-mappings`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function deletePageMapping(token: string, mappingId: number): Promise<void> {
  await api.delete(`/api/page-mappings/${mappingId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function updatePageMapping(
  token: string,
  mappingId: number,
  label: string | null
): Promise<PageMapping> {
  const response = await api.patch(`/api/page-mappings/${mappingId}`,
    { label },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function detectRoomsOCR(
  documentId: number,
  pageNumber: number,
  boundingBoxes: BoundingBox[]
): Promise<DetectedRoom[]> {
  const response = await api.post('/api/ocr/detect-rooms', {
    document_id: documentId,
    page_number: pageNumber,
    bounding_boxes: boundingBoxes
  });
  return response.data;
}

export async function createDocumentSet(
  token: string,
  projectId: number,
  name: string
): Promise<DocumentSet> {
  const response = await api.post(`/api/projects/${projectId}/document-sets`,
    { name },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function renameDocumentSet(
  token: string,
  documentSetId: number,
  name: string
): Promise<DocumentSet> {
  const response = await api.patch(`/api/document-sets/${documentSetId}`,
    { name },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function deleteDocumentSet(
  token: string,
  documentSetId: number
): Promise<void> {
  await api.delete(`/api/document-sets/${documentSetId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function uploadDocument(
  token: string,
  documentSetId: number,
  file: File,
  replaceDocumentId?: number,
  onUploadProgress?: (progress: number) => void
): Promise<UploadReplaceResponse> {
  const formData = new FormData();
  formData.append('file', file);

  let url = `/api/document-sets/${documentSetId}/documents`;
  if (replaceDocumentId) {
    url += `?replace_document_id=${replaceDocumentId}`;
  }

  const response = await api.post(url, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onUploadProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress(percentCompleted);
      }
    }
  });
  return response.data;
}

export async function activateDocument(token: string, documentId: number): Promise<Document> {
  const response = await api.post(`/api/documents/${documentId}/activate`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getDocumentOCRSummary(token: string, documentId: number): Promise<OCRSummaryResponse> {
  const response = await api.get(`/api/documents/${documentId}/ocr-summary`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getDocuments(token: string, documentSetId: number, includeArchived: boolean = false): Promise<Document[]> {
  const params = includeArchived ? '?include_archived=true' : '';
  const response = await api.get(`/api/document-sets/${documentSetId}/documents${params}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function renameDocument(token: string, documentId: number, filename: string): Promise<Document> {
  const response = await api.patch(`/api/documents/${documentId}`,
    { filename },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function deleteDocument(token: string, documentId: number): Promise<void> {
  await api.delete(`/api/documents/${documentId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export interface DocumentVersionResponse {
  id: number;
  document_set_id: number;
  filename: string;
  file_path: string;
  file_type: string;
  page_count: number;
  created_at: string;
  version: number;
  is_current: boolean;
  status: 'active' | 'pending_review' | 'archived';
  conversion_status: 'pending' | 'converting' | 'ready' | 'failed';
  conversion_progress: number;
  conversion_error: string | null;
  mapped_pages_count: number;
  uploader_id: number | null;
}

export async function getDocumentVersions(token: string, documentId: number): Promise<DocumentVersionResponse[]> {
  const response = await api.get(`/api/documents/${documentId}/versions`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export interface OCRSummaryResponse {
  document_id: number;
  conversion_status: string;
  pages_processed: number;
  mappings_created: number;
  unrecognized_rooms: Array<{room_number: string; page: number}>;
  unmapped_pages: number[];
  errors: string[];
  has_warnings: boolean;
}

export async function recalculateAllPageCounts(token: string, projectId: number): Promise<{ message: string }> {
  const response = await api.post(`/api/projects/${projectId}/recalculate-all-pages`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function createTrade(token: string, name: string): Promise<Trade> {
  const response = await api.post('/api/trades',
    { name },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function getTradesAuth(token: string): Promise<Trade[]> {
  const response = await api.get('/api/trades', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function deleteTrade(token: string, tradeId: number): Promise<void> {
  await api.delete(`/api/trades/${tradeId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function updateTrade(token: string, tradeId: number, name: string): Promise<Trade> {
  const response = await api.patch(`/api/trades/${tradeId}`, { name }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getProjectTasks(token: string, projectId: number): Promise<TaskWithDetails[]> {
  const response = await api.get(`/api/projects/${projectId}/tasks`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getStories(token: string, projectId: number): Promise<Story[]> {
  const response = await api.get(`/api/projects/${projectId}/stories`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function verifyTask(token: string, taskId: number): Promise<Task> {
  const response = await api.patch(`/api/tasks/${taskId}/verify`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function deleteTask(token: string, taskId: number): Promise<void> {
  await api.delete(`/api/tasks/${taskId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function updateTask(
  token: string,
  taskId: number,
  data: { description?: string; trade_id?: number | null; room_id?: number | null; status?: string; due_date?: string | null }
): Promise<Task> {
  const response = await api.patch(`/api/tasks/${taskId}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getProjectNotes(token: string, projectId: number): Promise<NoteWithDetails[]> {
  const response = await api.get(`/api/projects/${projectId}/notes`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function deleteNote(token: string, noteId: number): Promise<void> {
  await api.delete(`/api/notes/${noteId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function updateNote(token: string, noteId: number, content: string): Promise<NoteWithRooms> {
  const response = await api.patch(`/api/notes/${noteId}`,
    { content },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function updateNoteRooms(token: string, noteId: number, roomIds: number[]): Promise<NoteWithRooms> {
  const response = await api.put(`/api/notes/${noteId}/rooms`,
    roomIds,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function createMultiRoomNote(
  token: string,
  projectId: number,
  content: string,
  authorName: string,
  roomIds: number[]
): Promise<NoteWithRooms> {
  const response = await api.post(`/api/projects/${projectId}/notes`,
    { content, author_name: authorName, room_ids: roomIds },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function uploadNotePhoto(
  noteId: number,
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ note_id: number; photo_url: string; photo_original_name: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`/api/notes/${noteId}/photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    }
  });
  return response.data;
}

export async function deleteNotePhoto(token: string, noteId: number): Promise<void> {
  await api.delete(`/api/notes/${noteId}/photo`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export function getNotePhotoUrl(photoUrl: string | null): string | null {
  if (!photoUrl) return null;
  return `${API_BASE}${photoUrl}`;
}

// Task photo functions
export async function uploadTaskPhoto(
  taskId: number,
  file: File,
  token?: string,
  onProgress?: (progress: number) => void
): Promise<{ task_id: number; photo_url: string; photo_original_name: string; photo_tag: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {
    'Content-Type': 'multipart/form-data'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await api.post(`/api/tasks/${taskId}/photo`, formData, {
    headers,
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    }
  });
  return response.data;
}

export async function deleteTaskPhoto(token: string, taskId: number): Promise<void> {
  await api.delete(`/api/tasks/${taskId}/photo`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export function getTaskPhotoUrl(photoUrl: string | null): string | null {
  if (!photoUrl) return null;
  return `${API_BASE}${photoUrl}`;
}

export interface ActivityLog {
  id: number;
  project_id: number;
  room_id: number | null;
  action_type: 'task_created' | 'task_done' | 'task_verified' | 'note_added' | 'note_edited';
  description: string;
  created_at: string;
}

export async function getProjectActivity(
  token: string,
  projectId: number,
  actionType?: string,
  limit?: number
): Promise<ActivityLog[]> {
  const params = new URLSearchParams();
  if (actionType) params.append('action_type', actionType);
  if (limit) params.append('limit', limit.toString());

  const queryString = params.toString();
  const url = `/api/projects/${projectId}/activity${queryString ? `?${queryString}` : ''}`;

  const response = await api.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export interface OCRRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OCRRegionResponse extends OCRRegion {
  id: number;
  document_id: number;
}

export interface DetectRegionResponse {
  rooms: Array<{ number: string; name: string | null }>;
  raw_text: string;
}

export async function saveOCRRegions(
  token: string,
  documentId: number,
  regions: OCRRegion[]
): Promise<OCRRegionResponse[]> {
  const response = await api.post(`/api/ocr/documents/${documentId}/ocr-regions`, regions, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getOCRRegions(documentId: number): Promise<OCRRegionResponse[]> {
  const response = await api.get(`/api/ocr/documents/${documentId}/ocr-regions`);
  return response.data;
}

export async function detectOCRRegion(
  documentId: number,
  pageNumber: number,
  region: OCRRegion
): Promise<DetectRegionResponse> {
  const response = await api.post('/api/ocr/detect-region', region, {
    params: { document_id: documentId, page_number: pageNumber }
  });
  return response.data;
}

// ============================================================================
// WEB-FRIENDLY CONVERSION
// ============================================================================

export async function startConversion(token: string, documentId: number): Promise<{ message: string; document_id: number }> {
  const response = await api.post(`/api/documents/${documentId}/convert`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export interface ConversionStatus {
  status: 'pending' | 'converting' | 'ready' | 'failed';
  progress: number;
  page_count: number;
  error: string | null;
}

export async function getConversionStatus(token: string, documentId: number): Promise<ConversionStatus> {
  const response = await api.get(`/api/documents/${documentId}/conversion-status`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// ============================================================================
// DOCUMENT PAGES (WebP images)
// ============================================================================

export interface DocumentPagesMetadata {
  document_id: number;
  page_count: number;
  conversion_status: 'pending' | 'converting' | 'ready' | 'failed';
  conversion_progress: number;
  pages: number[];
}

export async function getDocumentPagesMetadata(documentId: number): Promise<DocumentPagesMetadata> {
  const response = await api.get(`/api/documents/${documentId}/pages`);
  return response.data;
}

export function getPageImageUrl(documentId: number, pageNumber: number): string {
  return `${API_BASE}/api/documents/${documentId}/pages/${pageNumber}`;
}

export function getPageThumbnailUrl(documentId: number, pageNumber: number): string {
  return `${API_BASE}/api/documents/${documentId}/pages/${pageNumber}/thumbnail`;
}

// ============================================================================
// USER MANAGEMENT
// ============================================================================

export interface User {
  id: number;
  username: string;
  name: string;
  is_admin: boolean;
  is_approved: boolean;
  must_change_password: boolean;
  notification_email: string | null;
  created_at: string | null;
}

export interface RegisterRequest {
  username: string;
  password: string;
  name: string;
  notification_email: string;
}

export async function registerUser(data: RegisterRequest): Promise<User> {
  const response = await api.post('/api/auth/register', data);
  return response.data;
}

export async function getPendingUsers(token: string): Promise<User[]> {
  const response = await api.get('/api/auth/users/pending', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function approveUser(token: string, userId: number): Promise<User> {
  const response = await api.post(`/api/auth/users/${userId}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function rejectUser(token: string, userId: number): Promise<void> {
  await api.post(`/api/auth/users/${userId}/reject`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

// ============================================================================
// ATTACHMENTS
// ============================================================================

// Upload multiple attachments to a project (auth required)
export async function uploadAttachments(
  token: string,
  projectId: number,
  files: File[],
  onProgress?: (progress: number) => void
): Promise<Attachment[]> {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));

  const response = await api.post(`/api/projects/${projectId}/attachments`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      }
    }
  });
  return response.data;
}

// List all attachments for a project (auth required)
export async function getProjectAttachments(token: string, projectId: number): Promise<Attachment[]> {
  const response = await api.get(`/api/projects/${projectId}/attachments`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// Update attachment display name (auth required)
export async function updateAttachment(token: string, attachmentId: number, displayName: string): Promise<Attachment> {
  const response = await api.put(`/api/attachments/${attachmentId}`, { display_name: displayName }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// Delete an attachment (auth required)
export async function deleteAttachment(token: string, attachmentId: number): Promise<void> {
  await api.delete(`/api/attachments/${attachmentId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

// Set room mappings for an attachment (auth required)
export async function setAttachmentRooms(token: string, attachmentId: number, roomIds: number[]): Promise<Attachment> {
  const response = await api.post(`/api/attachments/${attachmentId}/rooms`, { room_ids: roomIds }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// Get attachments for a room (public, no auth)
export async function getRoomAttachments(roomId: number): Promise<Attachment[]> {
  const response = await api.get(`/api/rooms/${roomId}/attachments`);
  return response.data;
}

// Get download URL for an attachment
export function getAttachmentDownloadUrl(attachmentId: number): string {
  return `/api/attachments/${attachmentId}/download`;
}

// Get thumbnail URL for an image attachment
export function getAttachmentThumbnailUrl(attachmentId: number): string {
  return `/api/attachments/${attachmentId}/thumbnail`;
}

// ============================================================================
// STORY PAGE MAPPINGS
// ============================================================================

// Create a story-level page mapping (auth required)
export async function createStoryPageMapping(
  token: string,
  storyId: number,
  documentId: number,
  pageNumber: number,
  label?: string
): Promise<PageMapping> {
  const response = await api.post(`/api/stories/${storyId}/page-mappings`, {
    document_id: documentId,
    page_number: pageNumber,
    label: label || null
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

// ============================================================================
// SCHEDULES
// ============================================================================

export type ScheduleType = string;
export type ExtractionStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface Schedule {
  id: number;
  project_id: number;
  schedule_type: ScheduleType;
  custom_name: string | null;
  schedule_date: string;
  extraction_mode: string;
  header_row: number;
  room_column: number;
  extraction_status: ExtractionStatus;
  extraction_error: string | null;
  version_count: number;
  current_version: {
    version_number: number;
    schedule_date: string;
    total_rows: number;
    matched_count: number;
    unmatched_count: number;
    extracted_data: any[];
    created_at: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleChange {
  id: number;
  change_type: 'added' | 'removed' | 'modified';
  room_number: string;
  change_summary: string;
  previous_data: any;
  current_data: any;
  is_acknowledged: boolean;
  created_at: string;
}

export interface ScheduleChangesSummary {
  schedule_id: number;
  version_from: number;
  version_to: number;
  total_changes: number;
  added: number;
  removed: number;
  modified: number;
  changes: ScheduleChange[];
}

export interface RoomScheduleRow {
  [key: string]: string | number;
}

export interface RoomScheduleData {
  schedule_id: number;
  schedule_type: ScheduleType;
  custom_name: string | null;
  schedule_date: string;
  filename: string;
  columns: string[];
  rows: RoomScheduleRow[];
  has_changes: boolean;
  unacknowledged_changes: number;
}

export async function uploadSchedule(
  token: string,
  projectId: number,
  file: File,
  scheduleType: ScheduleType,
  customName: string | null,
  scheduleDate: string,
  extractionMode: 'lattice' | 'stream',
  headerRow: number,
  roomColumn: number,
  onProgress?: (progress: number) => void
): Promise<Schedule> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('schedule_type', scheduleType);
  if (customName) formData.append('custom_name', customName);
  formData.append('schedule_date', scheduleDate);
  formData.append('extraction_mode', extractionMode);
  formData.append('header_row', headerRow.toString());
  formData.append('room_column', roomColumn.toString());

  const response = await api.post(`/api/projects/${projectId}/schedules`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    }
  });
  return response.data;
}

export async function getProjectSchedules(token: string, projectId: number): Promise<Schedule[]> {
  const response = await api.get(`/api/projects/${projectId}/schedules`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getSchedule(token: string, scheduleId: number): Promise<Schedule> {
  const response = await api.get(`/api/schedules/${scheduleId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function deleteSchedule(token: string, scheduleId: number): Promise<void> {
  await api.delete(`/api/schedules/${scheduleId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function updateSchedule(
  token: string,
  scheduleId: number,
  data: {
    schedule_type?: ScheduleType;
    custom_name?: string | null;
    extraction_mode?: 'lattice' | 'stream';
    header_row?: number;
    room_column?: number;
  }
): Promise<Schedule> {
  const response = await api.patch(`/api/schedules/${scheduleId}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function extractSchedule(token: string, scheduleId: number): Promise<Schedule> {
  const response = await api.post(`/api/schedules/${scheduleId}/extract`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getScheduleVersions(token: string, scheduleId: number): Promise<any[]> {
  const response = await api.get(`/api/schedules/${scheduleId}/versions`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function getScheduleChanges(
  token: string,
  scheduleId: number
): Promise<ScheduleChangesSummary> {
  const response = await api.get(`/api/schedules/${scheduleId}/changes`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
}

export async function acknowledgeScheduleChange(
  token: string,
  scheduleId: number,
  changeId: number
): Promise<void> {
  await api.post(`/api/schedules/${scheduleId}/changes/${changeId}/acknowledge`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export async function getRoomSchedules(roomId: number): Promise<RoomScheduleData[]> {
  const response = await api.get(`/api/rooms/${roomId}/schedules`);
  return response.data.schedules;
}

export interface ScheduleTypeInfo {
  type: string;
  version_count: number;
}

export async function getProjectScheduleTypes(token: string, projectId: number): Promise<ScheduleTypeInfo[]> {
  const response = await api.get(`/api/projects/${projectId}/schedule-types`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data.types;
}
